"use client";

import MinusIcon from "public/next-public/common/icons/minus-solid.svg";
import PlusIcon from "public/next-public/common/icons/plus-solid.svg";
import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "./quantityPicker.module.scss";

const QuantityPicker = ({
  value,
  min = 1,
  max = Number.MAX_SAFE_INTEGER,
  onChange,
  disabled = false,
  className,
  ariaLabel = "Quantity",
  yellowFocus = false,
}: QuantityPickerProps) => {
  const [draft, setDraft] = useState<string>(value.toString());
  // The field's focus ring is for focus from the keyboard; a click or tap makes the field active instead.
  // :focus-visible can't tell the two apart on a text field, so the field notes a pointer press.
  const [showsFocusRing, setShowsFocusRing] = useState(false);
  const focusedByPointer = useRef(false);
  const fieldRef = useRef<HTMLInputElement>(null);
  // A press that doesn't focus the field, like a scroll that starts on it, is forgotten at the next key.
  const forgetPointer = useCallback(() => {
    focusedByPointer.current = false;
  }, []);
  // Focus can move while the window is away, as with a Tab back in from the address bar, without the field
  // hearing of it, so what it noted is forgotten if the window comes back with focus somewhere else.
  const forgetPointerUnlessRefocused = useCallback(() => {
    if (document.activeElement !== fieldRef.current) {
      focusedByPointer.current = false;
    }
  }, []);

  useEffect(
    () => () => {
      document.removeEventListener("keydown", forgetPointer, true);
      window.removeEventListener("focus", forgetPointerUnlessRefocused);
    },
    [forgetPointer, forgetPointerUnlessRefocused],
  );

  useEffect(() => {
    setDraft(value.toString());
  }, [value]);

  const clamp = (next: number): number => Math.min(Math.max(next, min), max);
  const atMin = value <= min;
  const atMax = value >= max;

  const commitDraft = (): void => {
    const parsed = parseInt(draft, 10);
    if (isNaN(parsed)) {
      setDraft(value.toString());
      return;
    }
    const next = clamp(parsed);
    setDraft(next.toString());
    if (next !== value) {
      onChange(next);
    }
  };

  const step = (delta: number): void => {
    if ((delta < 0 && atMin) || (delta > 0 && atMax)) {
      return;
    }
    const next = clamp(value + delta);
    if (next !== value) {
      onChange(next);
    }
  };

  return (
    <div className={[styles.QuantityPicker, yellowFocus && styles.yellowFocus, className].filter(Boolean).join(" ")}>
      {/* At a limit a stepper gets aria-disabled rather than disabled, so a keyboard user who steps to the limit
          keeps focus on it instead of being dropped to the page. It leaves the Tab order like a disabled button,
          and screen readers still announce it as unavailable. */}
      <button
        type="button"
        className={styles.stepButton}
        onClick={() => step(-1)}
        disabled={disabled}
        aria-disabled={atMin || undefined}
        tabIndex={atMin ? -1 : undefined}
        aria-label="Decrease quantity"
      >
        <MinusIcon width={16} height={16} aria-hidden />
      </button>
      <input
        ref={fieldRef}
        className={[styles.numberField, showsFocusRing && styles.focusRing].filter(Boolean).join(" ")}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={draft}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => setDraft(event.target.value.replace(/[^0-9]/g, ""))}
        onPointerDown={() => {
          focusedByPointer.current = true;
          setShowsFocusRing(false);
          document.addEventListener("keydown", forgetPointer, { capture: true, once: true });
        }}
        onFocus={() => {
          setShowsFocusRing(!focusedByPointer.current);
          focusedByPointer.current = false;
        }}
        onBlur={(event) => {
          // When the tab or window loses focus the field stays the active element, and gets focus back as it was.
          const leftWindow = document.activeElement === event.currentTarget;
          focusedByPointer.current = leftWindow && !showsFocusRing;
          if (leftWindow) {
            window.addEventListener("focus", forgetPointerUnlessRefocused, { once: true });
          }
          setShowsFocusRing(false);
          commitDraft();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commitDraft();
          }
        }}
      />
      <button
        type="button"
        className={styles.stepButton}
        onClick={() => step(1)}
        disabled={disabled}
        aria-disabled={atMax || undefined}
        tabIndex={atMax ? -1 : undefined}
        aria-label="Increase quantity"
      >
        <PlusIcon width={16} height={16} aria-hidden />
      </button>
    </div>
  );
};

export default QuantityPicker;

export interface QuantityPickerProps {
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  className?: string;
  /** Accessible name for the quantity input. */
  ariaLabel?: string;
  /** Show focus as the design system's yellow focus stroke rather than the blue ring. */
  yellowFocus?: boolean;
}
